package com.rainbowclubs.rainbow.presentation.admin

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.data.models.MembershipStatus
import com.rainbowclubs.rainbow.data.models.UserRole
import com.rainbowclubs.rainbow.domain.use_cases.home.GetHomeDataUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class AdminViewModel(
    private val clubId: String,
    private val getHomeDataUseCase: GetHomeDataUseCase,
) : ViewModel() {

    sealed class AdminNavigationEvent {
        data class ManageEvents(val clubId: String) : AdminNavigationEvent()
        data class ManageClub(val clubId: String) : AdminNavigationEvent()
        data class ManageMembers(val clubId: String, val role: UserRole) : AdminNavigationEvent()
        data class TakeAttendance(val clubId: String, val eventId: String?) : AdminNavigationEvent()
    }

    private val _screenState = MutableStateFlow<ScreenState<AdminUiState>>(ScreenState.Loading)
    val screenState: StateFlow<ScreenState<AdminUiState>> = _screenState.asStateFlow()

    private val navigationIntentChannel = Channel<AdminNavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<AdminNavigationEvent> = navigationIntentChannel.receiveAsFlow()

    init {
        fetchAdminRole()
    }

    fun navigateToManageEvents() {
        navigationIntentChannel.trySend(AdminNavigationEvent.ManageEvents(clubId))
    }

    fun navigateToManageClub() {
        navigationIntentChannel.trySend(AdminNavigationEvent.ManageClub(clubId))
    }

    fun navigateToManageMembers(role: UserRole) {
        navigationIntentChannel.trySend(AdminNavigationEvent.ManageMembers(clubId, role))
    }

    fun navigateToTakeAttendance() {
        val eventId = (_screenState.value as? ScreenState.Content)?.data?.ongoingEventId
        navigationIntentChannel.trySend(AdminNavigationEvent.TakeAttendance(clubId, eventId))
    }

    fun fetchAdminRole() {
        viewModelScope.launch {
            _screenState.value = ScreenState.Loading
            getHomeDataUseCase(clubId)
                .onSuccess { data ->
                    val membership = data.membership
                    val role = if (membership?.status == MembershipStatus.APPROVED) {
                        membership.role
                    } else {
                        UserRole.MEMBER
                    }
                    _screenState.value = ScreenState.Content(
                        AdminUiState(
                            role = role,
                            ongoingEventId = data.ongoingEvent?.id,
                        )
                    )
                }
                .onFailure {
                    _screenState.value = ScreenState.Error(it)
                }
        }
    }
}
